import { ScheduleType } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import { startCase } from 'lodash-es';
import { i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { Chip } from '@frontend/design-system';
import { formatTime } from '../utils/get-event-accordion-body';
import { EventRenderer } from '../utils/render-event';
import { EventBox } from './event-renderer-item';

export const renderCondition: EventRenderer = (event) => {
  if (!('conditionEvent' in event)) return null;

  const { conditionEvent } = event;
  const name = conditionEvent.schedule?.scheduleName ?? conditionEvent.conditionName;
  const type = conditionEvent.schedule?.type;
  let chipColor: React.ComponentProps<typeof Chip>['variant'];
  if (type === ScheduleType.OPEN) chipColor = 'success';
  else if (type === ScheduleType.CLOSED) chipColor = 'critical';
  else chipColor = 'indigo';

  return (
    <div className='event-box-item' css={{ textAlign: 'center', position: 'relative' }}>
      <Chip isResponsive variant={chipColor}>
        {startCase(name)}
      </Chip>
    </div>
  );
};

export const renderDevice: EventRenderer = (event) => {
  if (!('deviceEvent' in event)) return null;

  const {
    createdAt,
    deviceEvent: { user, deviceName, deviceId },
  } = event;
  const startTime = formatTime(createdAt);
  const subtitle = user?.firstName ? `${user.firstName} ${user.lastName} (${deviceName})` : `${deviceName}`;

  return (
    <EventBox
      key={deviceId}
      iconName='cell-phone'
      title={i18next.t('Device', { ns: 'calls' })}
      subtitle={subtitle}
      endAdornment={startTime}
    />
  );
};

export const renderCallEnded: EventRenderer = (event, { callerName, userName }) => {
  if (!('callEndedEvent' in event)) return null;

  const { callEndedEvent } = event;
  const { callEndedAt, callEndedByCaller } = callEndedEvent;
  const endedAt = formatTime(callEndedAt);
  const callEndedByName = callEndedByCaller ? callerName : userName;
  const subTitle = !!callEndedByName ? i18next.t('{{ name }} hangs up', { ns: 'calls', name: callEndedByName }) : '';

  return (
    <EventBox
      key={callEndedAt}
      iconName='phone-hangup'
      title={i18next.t('Call Ended', { ns: 'calls' })}
      endAdornment={endedAt}
      subtitle={subTitle}
    />
  );
};

export const renderPlayMedia: EventRenderer = (event) => {
  if (!('playMediaEvent' in event)) return null;

  const { createdAt } = event;
  const { mediaId, mediaName } = event.playMediaEvent;
  const createdAtTime = formatTime(createdAt);
  const containsPhoneConfig = mediaName && /phone config/i.test(mediaName);
  const subtitle = containsPhoneConfig ? i18next.t('Voicemail Greeting', { ns: 'calls' }) : mediaName;

  return (
    <EventBox
      key={mediaId}
      iconName='speaker'
      title={i18next.t('Play Message', { ns: 'calls' })}
      subtitle={subtitle}
      endAdornment={createdAtTime}
    />
  );
};

export const renderForwarded: EventRenderer = (event) => {
  if (!('forwardedEvent' in event)) return null;

  const {
    forwardedEvent: { forwardedTo, forwardedNumberName },
    createdAt,
  } = event;
  const startTime = formatTime(createdAt);
  const subtitle = forwardedNumberName
    ? `${forwardedNumberName} ${formatPhoneNumber(forwardedTo)}`
    : `${formatPhoneNumber(forwardedTo)}`;

  return (
    <EventBox
      key={forwardedTo}
      iconName='send-forward'
      title={i18next.t('Forwarding Number', { ns: 'calls' })}
      subtitle={subtitle}
      endAdornment={startTime}
    />
  );
};

export const renderCallQueue: EventRenderer = (event) => {
  if (!('callQueueEvent' in event)) return null;

  const {
    createdAt,
    callQueueEvent: { callQueueName, callQueueId },
  } = event;

  const startTime = formatTime(createdAt);

  return (
    <EventBox
      key={callQueueId}
      iconName='call-queue'
      title={i18next.t('Call Queue', { ns: 'calls' })}
      subtitle={callQueueName}
      endAdornment={startTime}
    />
  );
};

export const renderCallGroup: EventRenderer = (event) => {
  if (!('callGroupEvent' in event)) return null;

  const {
    createdAt,
    callGroupEvent: { callGroupName, callGroupId },
  } = event;

  const startTime = formatTime(createdAt);

  return (
    <EventBox
      key={callGroupId}
      iconName='call-group'
      title={i18next.t('Call Group', { ns: 'calls' })}
      subtitle={callGroupName}
      endAdornment={startTime}
    />
  );
};
